import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  TwitterIcon,
  LinkedinIcon,
  SendIcon,
  ArrowUpIcon,
  DollarSignIcon,
  ShieldIcon,
  GlobeIcon,
  BookIcon,
  MailIcon, // ✅ ajouté pour l’email
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";

const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await Axios.post(API_END_POINTS.NEWS_LATTER_SUBSCRIBE, { email });

      if (response?.data?.message) {
        toast({
          title: "Subscription Successful!",
          description:  response?.data?.message || "You've been added to our newsletter.",
        });
        setEmail("");
      } else {
        toast({
          title: "Subscription Failed",
          description: response?.data?.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 text-gray-100 pt-16 pb-8 mt-20 relative">
      {/* Abstract decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500" />
      <div
        className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg"
        onClick={scrollToTop}
      >
        <ArrowUpIcon className="w-5 h-5 text-white" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Section with Logo and Newsletter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-blue-600 to-purple-600 flex items-center justify-center">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                DIGIKA.AI Freelance Hub
              </h2>
            </div>
            <p className="text-gray-400 max-w-md mb-6">
              Connecting talented freelancers with innovative projects through secure, blockchain-based transactions and smart contracts.
            </p>

            <div className="flex space-x-4">
              {/* 🌍 Digika.fr */}
              <a
                href="https://digika.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <GlobeIcon className="text-gray-300" size={20} />
              </a>

              {/* 🐦 Twitter/X */}
              <a
                href="https://x.com/digikaai"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <TwitterIcon className="text-gray-300" size={20} />
              </a>

              {/* 🔗 LinkedIn */}
              <a
                href="https://linkedin.com/company/digika"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <LinkedinIcon className="text-gray-300" size={20} />
              </a>

              {/* ✈️ Telegram */}
              <a
                href="https://t.me/dgkdigika"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <SendIcon className="text-gray-300" size={20} />
              </a>

              {/* 🪙 DGK Token */}
              <a
                href="https://dgktoken.com"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <GlobeIcon className="text-gray-300" size={20} />
              </a>

              {/* 📧 Email */}
              <a
                href="mailto:contact@digika.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors duration-300"
              >
                <MailIcon className="text-gray-300" size={20} />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-4 text-white">Stay Updated</h3>
            <p className="text-gray-400 mb-4">
              Subscribe to our newsletter for the latest updates on features, projects, and DGK token news.
            </p>
            <form onSubmit={handleSubscribe} className="flex max-w-md gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
              >
                <SendIcon className="w-4 h-4 mr-2" /> Subscribe
              </Button>
            </form>
          </div>
        </div>

        <Separator className="bg-gray-700 my-8" />

        {/* Features Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-xl">
            <DollarSignIcon className="h-8 w-8 text-blue-400 mb-3" />
            <h4 className="text-white text-lg font-medium mb-2">Multi-Currency Payments</h4>
            <p className="text-gray-400 text-sm">Flexible payouts in fiat, stablecoins, or DGK Token.</p>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-xl">
            <ShieldIcon className="h-8 w-8 text-green-400 mb-3" />
            <h4 className="text-white text-lg font-medium mb-2">Escrow Protection</h4>
            <p className="text-gray-400 text-sm">
              Funds managed securely by regulated PSPs (PayPal, Binance Pay) until project completion.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-xl">
            <GlobeIcon className="h-8 w-8 text-purple-400 mb-3" />
            <h4 className="text-white text-lg font-medium mb-2">Global Talent</h4>
            <p className="text-gray-400 text-sm">Access a worldwide pool of verified freelancers.</p>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-xl">
            <BookIcon className="h-8 w-8 text-yellow-400 mb-3" />
            <h4 className="text-white text-lg font-medium mb-2">DGK Utility</h4>
            <p className="text-gray-400 text-sm">
              Reduce fees, boost your profile visibility, and earn rewards by using DGK Token.
            </p>
          </div>
        </div>

        <Separator className="bg-gray-700 my-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center text-gray-500 text-sm">
          <div className="mb-4 md:mb-0">
            <p>© {currentYear} DIGIKA.AI Freelance Hub. All rights reserved.</p>
          </div>
          <div className="flex space-x-6">
            <Link to="/about" className="hover:text-blue-400 transition-colors duration-300">About Us</Link>
            <Link to="/support-center" className="hover:text-blue-400 transition-colors duration-300">Support</Link>
            <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors duration-300">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-blue-400 transition-colors duration-300">Terms</Link>
            <Link to="/cookie-policy" className="hover:text-blue-400 transition-colors duration-300">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
